'use strict';

import * as M3U8 from './../m3u8util';
import extend from '../../util/extend';
import SVPCustomEvent from '../../util/custom-event';
import * as UrlUtil from '../../util/url';
import * as domUtil from '../../util/dom';
import device from '../../util/device';
import VideoAttributes from '../../util/video-attributes';
import AudioTrack from '../../constants/audio-track';
import HlsJsSubtitles from './hls-js-subtitles';
import HlsNativeSubtitles from './hls-native-subtitles';

const Debug = require('../../util/debug')('hls-video');
const config = require('../../config');

const NAMESPACE = 'hls-video';

module.exports = (function () {

	let instances = {};
    const AUDIO_STORAGE_KEY = "audioTrack";


    function _extend(videoElement, uid) {

        instances[uid] = instances[uid] || {};

        extend(videoElement, {
            getCurrentTime: function () {
                return videoElement.currentTime;
            },

            getSrc: function () {
                // HLS.js sets a temporary URL (blob) as src attribute so we'll need to fetch the "real" URL form the HLS.js object
                const hlsjsObject = instances[uid].hlsjsObject;
                if (hlsjsObject) {
                    return hlsjsObject.url;
                }

                return videoElement.src;
            },

            getDuration: function () {
                return videoElement.liveDuration ? Math.max(videoElement.liveDuration, videoElement.currentTime) : videoElement.duration;
            },

            getVolume: function () {
                return videoElement.volume;
            },

            isMuted: function () {
                return videoElement.muted;
            },

            mute: function (shouldMute) {
                videoElement.muted = shouldMute;
            },

            getMetrics: function () {
                const isSVTCdn = this.getSrc().indexOf('cdn.svt') > 0;

                let metrics = {};
                if (videoElement.getVideoPlaybackQuality) {
                    metrics.droppedFrames = videoElement.getVideoPlaybackQuality().droppedVideoFrames;
                }
                metrics.bufferLevel = Math.round(videoElement.buffered.end(0) - videoElement.currentTime);
                metrics.dimensions = `${Math.round(videoElement.offsetWidth)} x ${Math.round(videoElement.offsetHeight)}`;
                metrics.resolution = `${videoElement.videoWidth} x ${videoElement.videoHeight}`;
                metrics.cdn = isSVTCdn ? 1 : 2;
                metrics.dash = false;

                return metrics;
            },

			seek: function (position) {
				if (videoElement.seekable.length > 0 && !videoElement.seeking) {
					// Make seekTo-position sane before seeking
					let seekTo = Math.min(Math.max(videoElement.seekable.start(0), position), videoElement.seekable.end(0));

                    // Due to bug in safari making video halt when scrubbing to
                    // the very end we extract a minimal amount from total duration
                    if (device.supportsHls() && seekTo >= videoElement.seekable.end(0)) {
                        seekTo -= 0.01;
                    }

					// hls.js don't like seeking to close to live edge
					if (videoElement.liveDuration && !device.supportsHls() && seekTo >= videoElement.seekable.end(0)) {
						seekTo -= 8;
					}

					if (seekTo !== Number.POSITIVE_INFINITY) {
						videoElement.currentTime = seekTo;
					}
				}
			},

			getMaxSeekable: function () {
				return videoElement.seekable.length > 0 ? videoElement.seekable.end(0) : 0;
			},

			setVolume: function (volume) {
				videoElement.volume = volume;
				videoElement.muted = (volume === 0);
			},

			showCaptions: function (shouldShow) {
                const subtitles = instances[uid].subtitles;

                if (subtitles && subtitles.isEnabled()) {
                    // Subtitles from manifest

                    if (shouldShow) {
                        subtitles.show();
                    } else {
                        subtitles.hide();
                    }


                } else {
                    // The "old way"
                    videoElement.parentNode.dispatchEvent(new SVPCustomEvent('captionsvisible', { detail: { shouldShow: shouldShow }}));
                }
			},

            getTextTracks: function () {
                return _getTextTracks(videoElement, instances[uid].hlsjsObject);
            },

            getAudioTracks: function () {
                return _getAudioTacks(videoElement, instances[uid].hlsjsObject);
            },

            setAudioTrack: function (audioTrackId) {
                _setAudioTrack(audioTrackId, videoElement, instances[uid].hlsjsObject);
                domUtil.setStoredData(AUDIO_STORAGE_KEY, audioTrackId);
            },

            reset: function () {
				if (videoElement.currentTime) {
					videoElement.currentTime = 0;
				}
			},

            unload: function () {
                domUtil.removeEvents(videoElement, 'play, pause', NAMESPACE);

                if (instances[uid].hlsjsObject) {
                    instances[uid].hlsjsObject.detachMedia();
                    instances[uid].hlsjsObject.destroy();
                }

                if (instances[uid].subtitles) {
                    instances[uid].subtitles.detach();
                }

                while (videoElement.lastChild) {
                    videoElement.removeChild(videoElement.lastChild);
                }

                videoElement.pause();
                videoElement.removeAttribute('src');
            }
        });
    }

    function _setAudioTrack(audioTrackId, videoElement, hlsjsObject) {
        if (videoElement.audioTracks) {
            for (let i = 0; i < videoElement.audioTracks.length; i++) {
                if (videoElement.audioTracks[i].language === audioTrackId) {
                    videoElement.audioTracks[i].enabled = true;
                }
            }
        }

        if (hlsjsObject) {
            for (let i = 0; i < hlsjsObject.audioTracks.length; i++) {
                if (hlsjsObject.audioTracks[i].lang === audioTrackId) {
                    hlsjsObject.audioTrack = hlsjsObject.audioTracks[i].id;
                }
            }
        }
    }

    function _setupHlsJs(videoUrl, videoElement, uid, api) {
        Debug.log('Using HLS.js', videoUrl);

        const hlsjsConfig = {
            //TODO: Channel this to normal debug-logger.
            debug: false,

            // Start to load HLS playlist and fragments
            // automatically after manifest is parsed.
            // Not to be confused with autoplay.
            autoStartLoad: true
        };

        require.ensure([], (require) => {
            const Hls = require('hls.js');
            const hlsjsObject = new Hls(hlsjsConfig);
            hlsjsObject.attachMedia(videoElement);
            hlsjsObject.on(Hls.Events.MEDIA_ATTACHED, () => {
                // Disable attributes in order to remove audio codec specs in playlist
                videoUrl = UrlUtil.setQueryParameters({attributes: 'off'}, videoUrl);
                Debug.log('HLSJS using url ' + videoUrl);
                hlsjsObject.loadSource(videoUrl);
            });

            hlsjsObject.on(Hls.Events.LEVEL_SWITCHING, (event, data) => {
                // The bitrate in hls is the sum of video, audio and subtitle bitrate.
                // We send the bitrateswitch as video bitrate. Although it is incorrect,
                // it is a good enough approximation since video is the heaviest media type.
                api.fire('bitrateswitch', { mediaType: 'video', bitrate: data.bitrate });
            });

            hlsjsObject.on(Hls.Events.ERROR, (event, data) => {
                const url = (data.frag && data.frag.url) ||
                            (data.context && data.context.url) ||
                            null;

                if (data.fatal === false) {
                    Debug.error('Non-fatal error thrown by hls.js was ignored.', event, data);

                    return;
                }

                if (data.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
                    api.fire('bufferEmpty');
                } else {
                    api.fire('error', {
                        player: 'hlsjs',
                        name: data.type,
                        id: data.details,
                        url: url
                    });
                }
            });

            const subtitles = new HlsJsSubtitles(hlsjsObject, videoElement, api, Hls);
            subtitles.initialize();

            instances[uid] = {
                hlsjsObject: hlsjsObject,
                subtitles
            };
        }, 'hls');
    }

    function _setupNativeHls(videoUrl, videoElement, uid, api) {
        Debug.log('Using native HLS', videoUrl);

        videoElement.setAttribute('src', videoUrl);

        if (device.isIOSNotSafari()) {
            // This is a hack for ensuring playability in Firefox and Chrome on
            // iOS 10. Hopefully it'll be fixed on their end once they've recompiled for iOS 10.
            // TODO: Remove once fixed.
            videoElement.dispatchEvent(new SVPCustomEvent('loadstart', { detail: {} }));
        }

        const subtitles = new HlsNativeSubtitles(videoElement, api);
        subtitles.initialize();

        instances[uid] = {
            hlsjsObject: null,
            subtitles
        };
    }

    function _getTextTracks(videoElement, hlsjsObject) {
        let tracks = [];

        if (videoElement.textTracks) {
            for (let i = 0; i < videoElement.textTracks.length; i++) {
                if (videoElement.textTracks[i].language === 'sv') {
                    tracks.push({
                        id: videoElement.textTracks[i].language
                    });
                }
            }
        } else if (hlsjsObject && hlsjsObject.textTracks) {
            for (let i = 0; i < hlsjsObject.textTracks.length; i++) {
                if (hlsjsObject.textTracks[i].lang === 'sv') {
                    tracks.push({
                        id: hlsjsObject.textTracks[i].lang
                    });
                }
            }
        }

        return tracks;
    }

    function _hasSpokenAudio(audioTracks) {
        if (audioTracks.length > 1) {
            for (let i = 0; i < audioTracks.length; i++) {
                if (audioTracks[i].id === AudioTrack.SPOKEN_TEXT) {
                    return true;
                }
            }
        }

        return false;
    }

    function _getAudioTacks(videoElement, hlsjsObject) {
        let activeAudioTrack = domUtil.getStoredData(AUDIO_STORAGE_KEY) || AudioTrack.DEFAULT_AUDIO;
        let tracks = [];

        if (videoElement.audioTracks) {
            for (let i = 0; i < videoElement.audioTracks.length; i++) {
                if (videoElement.audioTracks[i].language) {
                    tracks.push({
                        id: videoElement.audioTracks[i].language,
                        active: videoElement.audioTracks[i].language === activeAudioTrack
                    });
                }
            }
        }

        if (hlsjsObject && hlsjsObject.audioTracks) {
            for (let i = 0; i < hlsjsObject.audioTracks.length; i++) {
                tracks.push({
                    id: hlsjsObject.audioTracks[i].lang,
                    active: hlsjsObject.audioTracks[i].lang === activeAudioTrack
                });
            }
        }

        return tracks;
    }

    return {
        load: function (videoElement, videoUrl, metadata, videoConfig, api, options) {
            const uid = VideoAttributes.getUID(videoElement) || VideoAttributes.generateAndSetUID(videoElement);

            // Destroy previous instance
            if (typeof videoElement.unload === 'function') {
                videoElement.unload();
            }


            // Fetch available bitrates from the primary manifest URL (probably Akamai)
            M3U8.getManifest(videoUrl).then((manifest) => {

                // Setup a new manifest URL with bitrate limit parameters
                let modifiedVideoUrl = M3U8.setupMaxBitrate(manifest, videoElement, videoConfig);

                // Check if there is an alternative manifest URL (probably SVT-CDN switcher)
                const altUrl = UrlUtil.getQueryParameter('alt', modifiedVideoUrl);
                if (config.useAltHlsUrl && altUrl && device.isRedirectCapable()) {
                    const startBitrate = UrlUtil.getQueryParameter('__b__', modifiedVideoUrl);
                    const maxBitrate = UrlUtil.getQueryParameter('b', modifiedVideoUrl);

                    // Attach the bitrate limit parameters on the alt URL
                    modifiedVideoUrl = UrlUtil.setQueryParameters({
                        __b__: startBitrate,
                        b: maxBitrate
                    }, decodeURIComponent(altUrl));

                    // Get the manifest from the resolved URL
                    return M3U8.getManifest(modifiedVideoUrl).catch((e) => {
                        // If we fail to fetch it, let the video element try
                        // https://stackoverflow.com/questions/33044293/safari-xhr-ajax-requests-w-cross-domain-redirect-fails
                        return Promise.resolve(Object.assign({}, manifest, { url: modifiedVideoUrl }));
                    });
                } else {
                    return Promise.resolve(Object.assign({}, manifest, { url: modifiedVideoUrl }));
                }
            }).then((manifest) => {
                if (device.supportsHls()) {
                    _setupNativeHls(manifest.url, videoElement, uid, api);
                } else {
                    _setupHlsJs(manifest.url, videoElement, uid, api);
                }
            });

            const setInitialStartPosition = () => {
                const currentTime = VideoAttributes.getStartPosition(videoElement) || options.startPosition;

                if (currentTime && videoElement.currentTime !== currentTime) {
                    videoElement.currentTime = currentTime;
                }
            };

            const setPreferredAudioTrack = () => {
                let audioTrackId = domUtil.getStoredData(AUDIO_STORAGE_KEY) || AudioTrack.DEFAULT_AUDIO;
                if (audioTrackId === AudioTrack.DEFAULT_AUDIO) {
                    return;
                }

                let hlsjsObject = instances[uid].hlsjsObject;
                _setAudioTrack(audioTrackId, videoElement, hlsjsObject);
            };

            const metadataLoadedListener = () => {
                setInitialStartPosition();
                videoElement.liveDuration = metadata && metadata.timeshiftEnabled ? metadata.contentDuration : undefined;

                let hlsjsObject = instances[uid].hlsjsObject;

                if (_hasSpokenAudio(_getAudioTacks(videoElement, hlsjsObject))) {
                    setPreferredAudioTrack();
                    api.fire('audiotracksloaded');
                } else if (videoElement.audioTracks) {
                    domUtil.addEvents(videoElement.audioTracks, 'addtrack', () => {
                        setPreferredAudioTrack();
                        if(_hasSpokenAudio(_getAudioTacks(videoElement, hlsjsObject))) {
                            api.fire('audiotracksloaded');
                            domUtil.removeEvents(videoElement.audioTracks, 'addtrack', NAMESPACE);
                        }
                    }, NAMESPACE);
                }

                domUtil.removeEvents(videoElement, 'loadedmetadata, loadeddata', NAMESPACE);
            };

            domUtil.addEvents(videoElement, 'loadedmetadata, loadeddata', metadataLoadedListener, NAMESPACE);

            /*
             * Stop downloading new segments in a live stream while paused. This only work
             * on hls.js. Safari (using native hls) will continue to download new segments
             * even though the video is paused.
             */
            let fragmentLoadingEnabled = true;
            domUtil.addEvents(videoElement, 'play, pause', () => {
                const hlsjsObject = instances[uid].hlsjsObject;
                if (hlsjsObject && (metadata.live || metadata.simulcast)) {
                    if (videoElement.paused && fragmentLoadingEnabled) {
                        fragmentLoadingEnabled = false;
                        hlsjsObject.streamController.stopLoad();
                    }
                    else if (!videoElement.paused && !fragmentLoadingEnabled) {
                        hlsjsObject.streamController.startLoad();
                        fragmentLoadingEnabled = true;
                    }
                }
            }, NAMESPACE);

            _extend(videoElement, uid);

            return videoElement;
        }
    };
})();



// WEBPACK FOOTER //
// ./src/js/modules/hls/hls-video.js